import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "../../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { ACCENT_BLUE } from "../../../../theme/tokens";
import type { HireGenerationChange, HireSettings, HireShiftTypes } from "../hooks/useSuggestedHireEdits";
import { HIRE_SHIFT_TYPES, SHIFTS_PER_WEEK_MAX, SHIFTS_PER_WEEK_MIN } from "../hooks/useSuggestedHireEdits";
import { type ShiftLength, shiftsPerFte } from "../utils/suggestedHires";
import { SECTION_LABEL_SX, SETTINGS_TRIGGER_SX, ShiftScopeChips } from "./ShiftScopeChips";

type Props = {
	settings: HireSettings;
	/**
	 * Every shift type the department runs, and the ones currently in scope. This is THE shift filter for
	 * the page — it scopes the Suggested Hires rows AND the Recurring Gaps grid below, which used to
	 * carry a second copy of the same chips.
	 */
	shiftLabels: string[];
	enabledShiftLabels: string[];
	/** One chip toggled. The caller owns the store, and refuses a toggle that would leave none on. */
	onToggleShift: (label: string) => void;
	/**
	 * The shift lengths this department actually runs, from its own templates. The SHIFT LENGTH switch
	 * is only rendered when there are two of them: a department whose every template is 8h has no
	 * choice to make, and a control with one reachable value is a control that only ever misleads.
	 */
	shiftLengthOptions: ShiftLength[];
	/**
	 * Shift length / shifts-per-week. Both are query inputs, so the server regenerates — but the caller
	 * KEEPS stored edits: once the user has any, the stored list stays authoritative and only Revert
	 * Changes discards it. Shifts-per-week is also the employment badge's denominator, so the write alone
	 * re-derives every badge on the existing rows.
	 */
	onGenerationChange: (next: HireGenerationChange) => void;
};

/**
 * The switch's options, in the order they are drawn: ONE first.
 *
 * Order is load-bearing rather than cosmetic. `SystemFlatSwitch` falls back to index 0 when it cannot
 * match the active value, so the first option is what a degraded control claims — and the only safe
 * thing for it to claim is the mode the server actually packs when we send nothing.
 */
const SHIFT_TYPE_OPTIONS: readonly HireShiftTypes[] = HIRE_SHIFT_TYPES;

/** Popover over what shapes the suggestions: which shifts, how long one is, and how many a week. */
const HireSettingsPopover: FC<Props> = ({ settings, shiftLabels, enabledShiftLabels, shiftLengthOptions, onToggleShift, onGenerationChange }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	// Held as text so a mid-typing empty field doesn't snap back to a clamped number under the caret.
	const [perWeekDraft, setPerWeekDraft] = useState(String(settings.shiftsPerWeek));
	// Follow the value when it changes from outside the field — a shift-length switch can move it, and
	// Revert puts it back to the job's own default.
	useEffect(() => setPerWeekDraft(String(settings.shiftsPerWeek)), [settings.shiftsPerWeek]);

	const commitPerWeek = (raw: string) => {
		const parsed = Number(raw);
		if (!Number.isFinite(parsed)) {
			setPerWeekDraft(String(settings.shiftsPerWeek));
			return;
		}
		const clamped = Math.min(SHIFTS_PER_WEEK_MAX, Math.max(SHIFTS_PER_WEEK_MIN, Math.round(parsed)));
		setPerWeekDraft(String(clamped));
		if (clamped !== settings.shiftsPerWeek) {
			onGenerationChange({ shiftLength: settings.shiftLength, shiftsPerWeek: clamped });
		}
	};

	return (
		<SystemReactTinyPopover
			align="end"
			isOpen={open}
			onClickOutside={() => setOpen(false)}
			onClose={() => setOpen(false)}
			closeOnEscape
			positions={["bottom", "top", "left"]}
			content={
				<Box
					onClick={(event) => event.stopPropagation()}
					data-testid="hiring-hire-settings-panel"
					sx={{ display: "flex", flexDirection: "column", gap: 2, width: 300, p: 2 }}
				>
					{/* THE page's shift filter — the same control the Move tab's Settings carries. */}
					{shiftLabels.length > 1 && <ShiftScopeChips shiftLabels={shiftLabels} enabledShiftLabels={enabledShiftLabels} onToggleShift={onToggleShift} />}

					{/* Only a department that runs BOTH lengths gets the choice — see `shiftLengthOptions`. */}
					{shiftLengthOptions.length > 1 && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
							<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.settings.shiftLength")}</Typography>
							<SystemFlatSwitch
								options={shiftLengthOptions.map((hours) => ({
									value: hours,
									label: t("hiring.hire.hours", { hours }),
									"data-testid": `hiring-hire-settings-length-${hours}`,
								}))}
								/**
								 * The INDEX, not the value — and that is the whole bug this fixes. `activeIndexOrValue`
								 * is `number | T`, and the primitive resolves any `number` as an index, so passing the
								 * shift length (8 / 12) selected option 8 of a 2-option switch: no button got `.active`,
								 * the indicator never measured a button and so never rendered, and the control read as a
								 * uniformly tinted track with no active segment. The ambiguity is inherent to a
								 * number-VALUED switch, so it is resolved here rather than in the shared component,
								 * where changing the precedence would move every existing numeric caller.
								 */
								activeIndexOrValue={shiftLengthOptions.indexOf(settings.shiftLength)}
								// Matches the module's other switches (mode, Hire/Move): the white indicator pill
								// with accent ink, rather than a third treatment invented for this popover.
								activeColor={ACCENT_BLUE.INK}
								equalWidthOptions
								delayCallback={0}
								rootWidth={140}
								onSwitch={(option) => {
									// If shifts/week is still the FTE week of the OLD length, follow the new one — the user
									// never chose 5, it was the 8h default. A value they DID customise is kept.
									const isStillDefault = settings.shiftsPerWeek === shiftsPerFte(settings.shiftLength);
									onGenerationChange({
										shiftLength: option.value,
										shiftsPerWeek: isStillDefault ? shiftsPerFte(option.value) : settings.shiftsPerWeek,
									});
								}}
								data-testid="hiring-hire-settings-length"
							/>
						</Box>
					)}

					{/*
					 * Only a department running SEVERAL shift templates has this decision to make — the same
					 * rule the SHIFT LENGTH switch applies above, for the same reason: a control whose every
					 * option produces the identical schedule only ever misleads.
					 */}
					{shiftLabels.length > 1 && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
								<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.settings.shiftTypes")}</Typography>
								{/* The trade-off is the whole decision (headcount vs a mixed week), and it is too
								    long to sit under the switch without becoming the tallest thing in the popover. */}
								<InfoIconWithTooltip
									title={t("hiring.hire.settings.shiftTypesHint")}
									iconSx={{ fontSize: 14 }}
									dataTestId="hiring-hire-settings-shift-types-info"
								/>
							</Box>
							<SystemFlatSwitch
								options={SHIFT_TYPE_OPTIONS.map((value) => ({
									value,
									label: t(`hiring.hire.settings.shiftTypes.${value}`),
									"data-testid": `hiring-hire-settings-shift-types-${value}`,
								}))}
								/**
								 * The VALUE, not an index — the opposite of the block above, and deliberately so.
								 * `activeIndexOrValue` resolves any `number` as an index, and a legacy stored blob
								 * with no `shiftTypes` would make `OPTIONS.indexOf(...)` return -1, which the number
								 * branch passes through unclamped: `buttonRefs.current[-1]` is undefined, the sliding
								 * indicator never measures, and NO segment reads as active. A string hits the value
								 * branch, whose miss falls back to index 0 — which is why "one" is listed first.
								 */
								activeIndexOrValue={settings.shiftTypes}
								activeColor={ACCENT_BLUE.INK}
								equalWidthOptions
								delayCallback={0}
								// Wider than the 140 the SHIFT LENGTH switch uses: these labels are words, not "8h".
								rootWidth={180}
								onSwitch={(option) => onGenerationChange({ shiftTypes: option.value })}
								data-testid="hiring-hire-settings-shift-types"
							/>
						</Box>
					)}

					{/* The tour's anchor is this field, not the whole panel: a department with dozens of shift types
					    makes the panel taller than the screen, which left the tour's tooltip below the fold. */}
					<Box data-annie-target="hiring-hire-settings-per-week" sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
						<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.settings.shiftsPerWeek")}</Typography>
						<SystemValidationTextField
							value={perWeekDraft}
							inputType="number"
							maxValue={SHIFTS_PER_WEEK_MAX}
							onChange={(event) => setPerWeekDraft(event.target.value)}
							injectedProps={{
								onBlur: (event) => commitPerWeek((event.target as HTMLInputElement).value),
								onKeyDown: (event) => {
									if (event.key === "Enter") {
										commitPerWeek((event.target as HTMLInputElement).value);
									}
								},
							}}
							inputProps={{ inputProps: { min: SHIFTS_PER_WEEK_MIN, max: SHIFTS_PER_WEEK_MAX } }}
							formControlSx={{ width: 96 }}
							// The field has no validation copy, and the reserved line for it was an empty 19px
							// band under the last control in the popover.
							reserveHeightForHelperText={false}
							dataTestId="hiring-hire-settings-per-week"
						/>
					</Box>
				</Box>
			}
		>
			<SystemButton
				buttonVariant="secondary"
				size="compact"
				sx={SETTINGS_TRIGGER_SX}
				noFullWidthMobile
				leadingIcon={<SettingsOutlinedIcon sx={{ fontSize: 14 }} />}
				leadingIconGap="6px"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
				data-testid="hiring-hire-settings-button"
				data-annie-target="hiring-hire-settings"
			>
				{t("hiring.hire.settings.title")}
			</SystemButton>
		</SystemReactTinyPopover>
	);
};

export default HireSettingsPopover;
